import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';

import ConnectionFinder from '../../../api/ConnectionFinder';

// Oggetto riutilizzabile per colorare il focus dei componenti MUI (Input e Select)
const fuchsiaInputStyle = {
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: '#ff0f64',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#ff0f64',
  },
};

export default function EditPostModal({ open, postId, onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  // Configurazione dei moduli di Quill con gestione personalizzata delle immagini
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
        [{ 'color': [] }, { 'background': [] }], 
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: function() {
          const range = this.quill.getSelection();
          const url = window.prompt("Inserisci l'URL dell'immagine remota:");
          
          if (url) {
            try {
              const parsedUrl = new URL(url);
              if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
                setAlert({ severity: 'warning', message: 'Protocollo non sicuro. Usa http:// o https://' });
                return;
              }
              this.quill.insertEmbed(range ? range.index : 0, 'image', parsedUrl.toString());
            } catch (error) {
              setAlert({ severity: 'error', message: 'URL non valido.' });
            }
          }
        }
      }
    }
  }), [setAlert]);

  const loadPost = useCallback(async () => {
    if (!postId) return;
    setLoading(true);
    try {
      const res = await ConnectionFinder.get(`/blog/getPost/${postId}`, {
        headers: { "x-access-token": localStorage.getItem('token') }
      });
      setTitle(res.data.title || '');
      setContent(res.data.content || '');
      setCoverImage(res.data.cover_image || '');
      // Normalizza lo stato: consentiamo solo 'draft' o 'published'
      let loadedStatus = res.data.status;
      if (loadedStatus !== 'draft' && loadedStatus !== 'published') {
        loadedStatus = 'draft';
      }
      setStatus(loadedStatus);
      setAlert(null);
      setViewMode(false);
    } catch (error) {
      setAlert({ severity: 'error', message: 'Errore nel caricamento del post.' });
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (open && postId) {
      loadPost();
    }
  }, [open, postId, loadPost]);

  const handleSave = async () => {
    if (!title.trim()) {
      setAlert({ severity: 'warning', message: 'Inserisci un titolo.' });
      return;
    }

    setLoading(true);
    try {
      await ConnectionFinder.put(`/blog/updatePost/${postId}`, 
        { title, content, cover_image: coverImage, status },
        { headers: { "x-access-token": localStorage.getItem('token') } }
      );
      setAlert({ severity: 'success', message: 'Post aggiornato con successo.' });
      setTimeout(() => {
        onSave();
        onClose();
      }, 1500);
    } catch (error) {
      setAlert({ severity: 'error', message: 'Errore nell\'aggiornamento del post.' });
      console.error('Error updating post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPost = () => {
    setViewMode(!viewMode);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{viewMode ? 'Anteprima Post' : 'Modifica Post'}</span>
        <IconButton
          onClick={handleViewPost}
          size="small"
          title={viewMode ? 'Torna alla modifica' : 'Visualizza anteprima'}
          sx={{
            backgroundColor: '#ff0f64',
            color: '#ffffff',
            borderRadius: '6px',
            padding: '6px',
            '&:hover': {
              backgroundColor: '#d10b52',
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {alert && (
          <Alert severity={alert.severity} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}

        {loading && !viewMode ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress sx={{ color: '#ff0f64' }} />
          </Box>
        ) : viewMode ? (
          // Modalità visualizzazione (Anteprima coerente)
          <Box sx={{ padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
            {coverImage && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img 
                  src={coverImage} 
                  alt="Immagine di copertina" 
                  style={{ 
                    maxHeight: '300px', 
                    maxWidth: '100%', 
                    objectFit: 'cover', 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }} 
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </Box>
            )}
            <h2 style={{ marginTop: 0, color: '#333' }}>{title}</h2>
            <div 
              className="ql-editor" /* Mantiene la formattazione originale di Quill */
              style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: 'white', 
                borderRadius: '6px',
                border: '1px solid #eaeaea',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
              dangerouslySetInnerHTML={{ __html: content }} 
            />
          </Box>
        ) : (
          // Modalità modifica
          <>
            <TextField
              fullWidth
              label="Titolo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              disabled={loading}
              sx={fuchsiaInputStyle} // Applica il focus fucsia
            />

            <TextField
              fullWidth
              label="URL Immagine di Copertina"
              value={coverImage}
              onChange={(e) => setCoverImage(e.target.value)}
              margin="normal"
              placeholder="https://esempio.it/immagine.jpg"
              disabled={loading}
              sx={fuchsiaInputStyle}
            />

            {coverImage && (
              <Box sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
                <img 
                  src={coverImage} 
                  alt="Anteprima Copertina" 
                  style={{ maxHeight: '120px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e0e0e0' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </Box>
            )}

            <FormControl fullWidth margin="normal" disabled={loading} sx={fuchsiaInputStyle}>
              <InputLabel>Stato</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Stato"
              >
                <MenuItem value="draft">Bozza</MenuItem>
                <MenuItem value="published">Pubblicato</MenuItem>
              </Select>
            </FormControl>

            {/* Contenitore Quill personalizzato con Bordo, Ombra e Focus coerenti */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#555' }}>
                Contenuto
              </label>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:focus-within': {
                    borderColor: '#ff0f64',
                    boxShadow: '0 4px 12px rgba(255, 15, 100, 0.15)',
                  },
                  '& .ql-toolbar.ql-snow': {
                    border: 'none',
                    borderBottom: '1px solid #eaeaea',
                    backgroundColor: '#f8f9fa',
                  },
                  '& .ql-container.ql-snow': {
                    border: 'none',
                    minHeight: '250px',
                  }
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={modules}
                  placeholder="Scrivi qui il tuo contenuto..."
                  readOnly={loading}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ color: '#555' }}>
          Chiudi
        </Button>
        {!viewMode && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#ff0f64',
              color: '#ffffff',
              fontWeight: 'medium',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#d10b52',
              },
            }}
          >
            {loading ? 'Salvando...' : 'Salva modifiche'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
